import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = '';

const COLORS = ['#1a73e8','#ea4335','#34a853','#fbbc04','#ff6d01','#46bdc6','#7b1fa2','#e91e63','#00bcd4','#8bc34a'];

function SimpleChart({ chart }) {
  if (!chart) return null;
  const { chartType, title, labels, datasets } = chart;
  if (!labels || !datasets || !datasets[0]) return null;

  const data = datasets[0].data || [];
  const maxVal = Math.max(...data, 1);

  if (chartType === 'pie') {
    const total = data.reduce((a, b) => a + b, 0) || 1;
    let cumulative = 0;
    return (
      <div style={{ marginTop: 16 }}>
        <h4 style={{ textAlign: 'center', color: '#333', marginBottom: 12 }}>{title}</h4>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap' }}>
          <svg width="200" height="200" viewBox="-1 -1 2 2" style={{ transform: 'rotate(-90deg)' }}>
            {data.map((val, i) => {
              const pct = val / total;
              const start = cumulative;
              cumulative += pct;
              const x1 = Math.cos(2 * Math.PI * start);
              const y1 = Math.sin(2 * Math.PI * start);
              const x2 = Math.cos(2 * Math.PI * cumulative);
              const y2 = Math.sin(2 * Math.PI * cumulative);
              const large = pct > 0.5 ? 1 : 0;
              return (
                <path key={i} d={`M 0 0 L ${x1} ${y1} A 1 1 0 ${large} 1 ${x2} ${y2} Z`}
                  fill={COLORS[i % COLORS.length]} stroke="white" strokeWidth="0.02"/>
              );
            })}
          </svg>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, justifyContent: 'center' }}>
            {labels.map((l, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span style={{ width: 12, height: 12, borderRadius: 2, background: COLORS[i % COLORS.length], display: 'inline-block' }}/>
                {l}: {data[i]} ({Math.round((data[i] / total) * 100)}%)
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // bar or line
  return (
    <div style={{ marginTop: 16 }}>
      <h4 style={{ textAlign: 'center', color: '#333', marginBottom: 12 }}>{title}</h4>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 200, padding: '0 10px', borderBottom: '2px solid #e0e0e0' }}>
        {data.map((val, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: '#666' }}>{val}</span>
            <div style={{
              width: '80%', maxWidth: 60,
              height: Math.max(4, (val / maxVal) * 180),
              background: `linear-gradient(180deg, ${COLORS[i % COLORS.length]}, ${COLORS[i % COLORS.length]}dd)`,
              borderRadius: '4px 4px 0 0', transition: 'height 0.5s ease'
            }}/>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 4, padding: '6px 10px' }}>
        {labels.map((l, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {l}
          </div>
        ))}
      </div>
      {datasets.length > 1 && (
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 8 }}>
          {datasets.map((ds, i) => (
            <span key={i} style={{ fontSize: 12, color: COLORS[i % COLORS.length], fontWeight: 'bold' }}>{ds.label}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function DataAgent() {
  const navigate = useNavigate();
  const [dataInfo, setDataInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [chart, setChart] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    setDataInfo(null);
    setAnswer('');
    setChart(null);
    setUploadMsg('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API}/data/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.error) {
        setUploadMsg(data.error);
      } else {
        setDataInfo(data);
      }
    } catch (err) {
      setUploadMsg('업로드 실패');
    }
    setLoading(false);
  };

  const handleAnalyze = async (q) => {
    const askQuestion = q || question;
    if (!dataInfo) return;
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/data/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data_text: dataInfo.data_text, question: askQuestion, columns: dataInfo.columns })
      });
      const data = await res.json();
      setAnswer(data.answer || data.error);
    } catch (err) {
      setAnswer('분석 실패');
    }
    setAnalyzing(false);
  };

  const handleChart = async (req) => {
    if (!dataInfo) return;
    setChartLoading(true);
    try {
      const res = await fetch(`${API}/data/chart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data_text: dataInfo.data_text, columns: dataInfo.columns, chart_request: req || '' })
      });
      const data = await res.json();
      setChart(data.chart);
    } catch (err) {
      setChart(null);
    }
    setChartLoading(false);
  };

  const quickQuestions = [
    '이 데이터의 전체 요약과 핵심 인사이트를 알려주세요.',
    '이상치나 특이한 패턴이 있는지 분석해주세요.',
    '각 컬럼 간의 관계와 상관성을 분석해주세요.',
    '데이터 품질 이슈(결측치, 중복 등)를 점검해주세요.',
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      <div style={{
        background: 'linear-gradient(135deg, #0288d1, #03a9f4)',
        padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16
      }}>
        <button onClick={() => navigate('/')}
          style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
            width: 36, height: 36, borderRadius: 8, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 4l-6 6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div>
          <div style={{ color: 'white', fontSize: 18, fontWeight: 'bold' }}>데이터 분석 에이전트</div>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>CLSOFTLAB AI SERVICE</div>
        </div>
      </div>

      <div style={{ maxWidth: 1000, margin: '24px auto', padding: '0 20px 40px' }}>
        {/* 업로드 */}
        <div style={{ background: 'white', borderRadius: 8, border: '1px solid #ddd', padding: 20, marginBottom: 20 }}>
          <h2 style={{ marginTop: 0, fontSize: 17 }}>데이터 파일 업로드</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {['CSV','XLSX','XLS','TSV'].map(ext => (
              <span key={ext} style={{
                padding: '2px 10px', borderRadius: 12, fontSize: 13,
                background: '#e1f5fe', color: '#0288d1', border: '1px solid #b3e5fc',
              }}>{ext}</span>
            ))}
          </div>
          <input type="file" accept=".csv,.xlsx,.xls,.tsv" onChange={handleUpload} />
          {loading && <p style={{ color: '#0288d1', marginTop: 10 }}>파일 분석 중...</p>}
          {uploadMsg && <p style={{ color: '#c62828', marginTop: 10 }}>{uploadMsg}</p>}
        </div>

        {dataInfo && (
          <>
            {/* 데이터 요약 */}
            <div style={{ background: 'white', borderRadius: 8, border: '1px solid #ddd', padding: 20, marginBottom: 20 }}>
              <h2 style={{ marginTop: 0, fontSize: 17 }}>데이터 요약</h2>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <div style={{ padding: '12px 20px', background: '#e1f5fe', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 'bold', color: '#0288d1' }}>{dataInfo.total_rows.toLocaleString()}</div>
                  <div style={{ fontSize: 13, color: '#666' }}>행</div>
                </div>
                <div style={{ padding: '12px 20px', background: '#e8f5e9', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 'bold', color: '#2e7d32' }}>{dataInfo.total_columns}</div>
                  <div style={{ fontSize: 13, color: '#666' }}>컬럼</div>
                </div>
                <div style={{ padding: '12px 20px', background: '#fef3e2', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 'bold', color: '#e67700' }}>{dataInfo.column_stats.filter(c => c.is_numeric).length}</div>
                  <div style={{ fontSize: 13, color: '#666' }}>숫자 컬럼</div>
                </div>
              </div>

              {/* 컬럼 통계 */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>컬럼명</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>타입</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>유효값</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>고유값</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>최소/최대/평균</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataInfo.column_stats.map((col, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 'bold' }}>{col.name}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: 10, fontSize: 11,
                            background: col.is_numeric ? '#e1f5fe' : '#f3e8fd',
                            color: col.is_numeric ? '#0288d1' : '#7b1fa2',
                          }}>{col.is_numeric ? '숫자' : '텍스트'}</span>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>{col.non_empty}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>{col.unique}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, color: '#666' }}>
                          {col.is_numeric ? `${col.min} / ${col.max} / ${col.avg}` : (col.top_values ? col.top_values.slice(0,2).map(v => v.value).join(', ') : '-')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 데이터 미리보기 */}
              <h3 style={{ fontSize: 15, marginTop: 20, marginBottom: 8 }}>데이터 미리보기 (최대 20행)</h3>
              <div style={{ overflowX: 'auto', maxHeight: 300, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      {dataInfo.preview[0] && dataInfo.preview[0].map((h, i) => (
                        <th key={i} style={{ padding: '6px 10px', background: '#e1f5fe', borderBottom: '2px solid #0288d1', textAlign: 'left', whiteSpace: 'nowrap', position: 'sticky', top: 0 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dataInfo.preview.slice(1).map((row, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                        {row.map((cell, j) => (
                          <td key={j} style={{ padding: '6px 10px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 빠른 분석 + 차트 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
              <div style={{ background: 'white', borderRadius: 8, border: '1px solid #ddd', padding: 20 }}>
                <h3 style={{ marginTop: 0, fontSize: 15, color: '#0288d1' }}>빠른 분석</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {quickQuestions.map((q, i) => (
                    <button key={i} onClick={() => { setQuestion(q); handleAnalyze(q); }}
                      disabled={analyzing}
                      style={{
                        padding: '10px 14px', background: '#f5f5f5', border: '1px solid #e0e0e0',
                        borderRadius: 6, cursor: 'pointer', textAlign: 'left', fontSize: 13,
                        color: '#333', lineHeight: 1.4
                      }}>{q}</button>
                  ))}
                </div>
              </div>
              <div style={{ background: 'white', borderRadius: 8, border: '1px solid #ddd', padding: 20 }}>
                <h3 style={{ marginTop: 0, fontSize: 15, color: '#0288d1' }}>차트 생성</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button onClick={() => handleChart('')} disabled={chartLoading}
                    style={{ padding: '10px 14px', background: '#e1f5fe', border: '1px solid #b3e5fc', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#0288d1', fontWeight: 'bold' }}>
                    {chartLoading ? '생성중...' : '자동 차트 생성'}
                  </button>
                  <button onClick={() => handleChart('상위 항목을 비교하는 막대 차트')} disabled={chartLoading}
                    style={{ padding: '10px 14px', background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                    막대 차트
                  </button>
                  <button onClick={() => handleChart('비율을 보여주는 파이 차트')} disabled={chartLoading}
                    style={{ padding: '10px 14px', background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                    파이 차트
                  </button>
                </div>
                {chart && <SimpleChart chart={chart} />}
              </div>
            </div>

            {/* 자유 질문 */}
            <div style={{ background: 'white', borderRadius: 8, border: '1px solid #ddd', padding: 20 }}>
              <h2 style={{ marginTop: 0, fontSize: 17 }}>데이터에 대해 질문하기</h2>
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <input type="text" value={question} onChange={e => setQuestion(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !analyzing) handleAnalyze(); }}
                  placeholder="예: 매출이 가장 높은 항목은? 월별 추이를 분석해줘"
                  style={{ flex: 1, padding: 10, fontSize: 15, borderRadius: 6, border: '1px solid #ccc' }} />
                <button onClick={() => handleAnalyze()} disabled={analyzing}
                  style={{
                    padding: '10px 24px', background: analyzing ? '#ccc' : '#0288d1', color: 'white',
                    border: 'none', borderRadius: 6, cursor: analyzing ? 'not-allowed' : 'pointer',
                    fontSize: 14, fontWeight: 'bold', whiteSpace: 'nowrap'
                  }}>
                  {analyzing ? '분석중...' : '분석'}
                </button>
              </div>
              {answer && (
                <div style={{ padding: 20, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef', lineHeight: 1.8 }}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h3: ({children}) => <h3 style={{ marginTop: 16, marginBottom: 8, fontSize: 16, color: '#0288d1' }}>{children}</h3>,
                      strong: ({children}) => <strong style={{ color: '#c62828' }}>{children}</strong>,
                      ul: ({children}) => <ul style={{ paddingLeft: 20, margin: '8px 0' }}>{children}</ul>,
                      ol: ({children}) => <ol style={{ paddingLeft: 20, margin: '8px 0' }}>{children}</ol>,
                      li: ({children}) => <li style={{ marginBottom: 4 }}>{children}</li>,
                      p: ({children}) => <p style={{ margin: '8px 0' }}>{children}</p>,
                      table: ({children}) => <table style={{ borderCollapse: 'collapse', width: '100%', margin: '10px 0' }}>{children}</table>,
                      th: ({children}) => <th style={{ border: '1px solid #dee2e6', padding: '8px 12px', background: '#e9ecef', textAlign: 'left' }}>{children}</th>,
                      td: ({children}) => <td style={{ border: '1px solid #dee2e6', padding: '8px 12px' }}>{children}</td>,
                    }}
                  >{answer}</ReactMarkdown>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DataAgent;
